"use client";

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Building2, 
  Bell, 
  MapPin, 
  Calendar, 
  FileText, 
  LogOut, 
  Globe,
  Database
} from "lucide-react";
import { cn } from "../lib/utils";
import { motion } from "framer-motion";
import { Sidebar, SidebarBody, SidebarLink, SidebarMenu, SidebarMenuItem, SidebarMenuButton } from "./ui/sidebar";
// Dropdown menu imports removed - simplified to signout button only
import { useAuth } from "../contexts/AuthContext";

interface SidebarProps {
  activePage?: string;
}

export default function AppSidebar({ activePage }: SidebarProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Auto-collapse when not hovering
  useEffect(() => {
    if (!isHovering) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isHovering]);

  // Use location.pathname if activePage not provided
  const currentPage = activePage || location.pathname;

  const links = [
    {
      label: "Business",
      href: "/",
      icon: <LayoutDashboard className="h-5 w-5 shrink-0 text-neutral-400" />,
    },
    // Hidden from sidebar but routes still exist:
    // {
    //   label: "Alerts",
    //   href: "/alerts",
    //   icon: <Bell className="h-5 w-5 shrink-0 text-neutral-400" />,
    // },
    // Project profiles are the main product (no Beta label)
    {
      label: "Projects",
      href: "/projects",
      icon: <Building2 className="h-5 w-5 shrink-0 text-neutral-400" />,
    },
    {
      label: "Acquisitions",
      href: "/acquisitions",
      icon: <FileText className="h-5 w-5 shrink-0 text-neutral-400" />,
    },
    {
      label: "Sources",
      href: "/municipalities",
      icon: <MapPin className="h-5 w-5 shrink-0 text-neutral-400" />,
    },
  ];

  return (
    <Sidebar 
      open={open} 
      setOpen={setOpen}
      onMouseEnter={() => {
        setIsHovering(true);
        if (!open) setOpen(true);
      }}
      onMouseLeave={() => {
        // Mouse has left the sidebar, collapse it
        setIsHovering(false);
      }}
    >
      <SidebarBody className="justify-between gap-10">
        <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto">
          {open ? <Logo /> : <LogoIcon />}
          <div className="mt-8 flex flex-col gap-2">
            {links.map((link, idx) => (
              <SidebarLink key={idx} link={link} open={open} />
            ))}
          </div>
        </div>
        <div>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                className={cn(
                  "text-neutral-400 hover:text-white hover:bg-white/5",
                  !open && "justify-center px-2"
                )}
                onClick={logout}
              >
                <LogOut className={cn(
                  "shrink-0",
                  open ? "h-5 w-5" : "h-4 w-4"
                )} />
                {open && (
                  <span className="ml-2 text-sm font-medium">Sign out</span>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
      </SidebarBody>
    </Sidebar>
  );
}

export const Logo = () => {
  return (
    <a
      href="/"
      className="relative z-20 flex items-center space-x-2 py-1 text-sm font-normal text-white"
    >
      <div className="h-8 w-8 shrink-0 rounded-lg bg-primary flex items-center justify-center shadow-sm">
        <Building2 className="h-5 w-5 text-black" />
      </div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="font-bold whitespace-pre text-white"
      >
        Cityscrape
      </motion.span>
    </a>
  );
};

export const LogoIcon = () => {
  return (
    <a
      href="/"
      className="relative z-20 flex items-center space-x-2 py-1 text-sm font-normal text-white"
    >
      <div className="h-8 w-8 shrink-0 rounded-lg bg-primary flex items-center justify-center shadow-sm">
        <Building2 className="h-5 w-5 text-black" />
      </div>
    </a>
  );
};
